import { CircleCheck, Info } from 'lucide-react';

import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';

interface PlanCardProps {
  title: string;
  description: string;
  price: string;
  priceSuffix?: string;
  features: {
    text: string;
    tooltip?: string;
  }[];
  button?: React.ReactNode;
  highlight?: boolean;
  badge?: string;
}

export function PlanCard({
  title,
  description,
  price,
  priceSuffix,
  features,
  button,
  highlight,
  badge,
}: PlanCardProps) {
  return (
    <div
      className={`relative flex min-h-px min-w-0 flex-1 grow flex-col items-start justify-start gap-10 self-stretch rounded-xl bg-white p-8 ${
        highlight ? 'border' : 'border-border border'
      }`}
    >
      {highlight ? null : (
        <div
          className="border-border pointer-events-none absolute inset-0 rounded-xl border"
          aria-hidden="true"
        />
      )}
      <div className="flex w-full flex-1 flex-col gap-6">
        <div className="relative flex w-full flex-col gap-3">
          <div className="text-primary font-sans text-lg leading-7 font-semibold">{title}</div>
          <div className="text-muted-foreground h-14 font-sans text-sm leading-5 font-normal">
            {description}
          </div>
          {badge && (
            <div className="text-primary absolute top-1 right-0 rounded-full border px-2.5 py-0.5 font-sans text-xs leading-4 font-semibold">
              {badge}
            </div>
          )}
        </div>
        <div className="flex w-full flex-row items-end gap-1">
          <div className="text-primary font-sans text-4xl leading-10 font-semibold">{price}</div>
          {priceSuffix && (
            <div className="text-muted-foreground font-sans text-base leading-6 font-normal">
              {priceSuffix}
            </div>
          )}
        </div>
        <div className="flex w-full flex-1 flex-col gap-4">
          {features?.map((feature) => (
            <PlanFeatureRow key={feature.text} text={feature.text} tooltip={feature.tooltip} />
          ))}
        </div>
      </div>

      {button}
    </div>
  );
}

interface PlanFeatureRowProps {
  text: string;
  tooltip?: string;
}

function PlanFeatureRow({ text, tooltip }: PlanFeatureRowProps) {
  return (
    <div className="flex w-full flex-row items-start justify-between gap-1.5">
      <CircleCheck className="size-5" />
      <div className="text-muted-foreground flex-1 grow font-sans text-sm leading-5 font-normal">
        {text}
      </div>
      {tooltip && (
        <TooltipFlat content={tooltip}>
          <Info className="size-4 text-zinc-400" />
        </TooltipFlat>
      )}
    </div>
  );
}
